import React, {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import RichTextFormField from "@common/components/forms/rich-text-form-field/rich-text-form-field";
import AppButton from "@common/components/app-button";
import {ReactComponent as EyeIcon} from "@assets/icons/eye.svg";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {VisitRequirementPreviewModalData} from "./visit-requirement-preview-model";
import {MediaOptions, TextOptions} from "@common/components/forms/rich-text-form-field/tools/tools-types.enum";
import {useAppMetadata} from "../../core/hooks/use-app-metadata";
import NotificationService from "@common/services/notification.service";
import {classNames} from "@utils/class-names";
import {getTextLengthFromJson} from "@common/rich-text-length";

interface VisitRequirementFormValues {
    visitRequired: string;
    visitOptional: string;
}

const MAX_LENGTH = 600;

const VisitRequirementSection: React.FC = () => {
    const {showModal} = useModalWithData<VisitRequirementPreviewModalData>(ModalIds.VISIT_REQUIREMENT_PREVIEW);
    const {duringVisitInfo, updateOptionalDuringVisit, updateRequiredDuringVisit} = useAppMetadata();
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const form = useForm<VisitRequirementFormValues>({
        mode: "onSubmit",
        defaultValues: {
            visitRequired: duringVisitInfo?.requiredDuringVisit ?? "",
            visitOptional: duringVisitInfo?.optionalDuringVisit ?? "",
        },
    });
    const {reset, getValues, watch, formState: {isSubmitting}} = form;
    const visitRequiredValue = watch("visitRequired");
    const visitOptionalValue = watch("visitOptional");

    const handlePreview = () => {
        showModal({
            required: getValues("visitRequired"),
            optional: getValues("visitOptional"),
        });
    };

    const getLength = (value: string) => getTextLengthFromJson(value);
    const isOverLimit = (value: string) => getLength(value) > MAX_LENGTH;

    const handleSave = async () => {
        (document.activeElement as HTMLElement)?.blur();

        setShowValidationErrors(true);

        const requiredValue = getValues("visitRequired");
        const optionalValue = getValues("visitOptional");

        const invalidFields = [
            isOverLimit(requiredValue) && `Pole „Wymagane” przekracza limit ${MAX_LENGTH} znaków.`,
            isOverLimit(optionalValue) && `Pole „Opcjonalne” przekracza limit ${MAX_LENGTH} znaków.`,
        ].filter(Boolean) as string[];

        if (invalidFields.length) {
            NotificationService.warning(invalidFields.join(" "));
            return;
        }

        try {
            await Promise.all([
                updateRequiredDuringVisit(requiredValue),
                updateOptionalDuringVisit(optionalValue),
            ]);

            setShowValidationErrors(false);
            NotificationService.success("Zmiany zostały zapisane.");
        } catch (error) {
            NotificationService.error("Nie udało się zapisać zmian.", error);
        }
    };

    useEffect(() => {
        if (duringVisitInfo) {
            reset({
                visitRequired: duringVisitInfo.requiredDuringVisit ?? "",
                visitOptional: duringVisitInfo.optionalDuringVisit ?? "",
            });
        }
    }, [duringVisitInfo, reset]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-on-surface">
                    Przygotowanie do wizyty
                </h2>
                <p className="text-base text-on-surface/80 leading-5 font-normal">
                    Określ, co pacjent powinien zabrać lub wiedzieć przed wizytą. Treść będzie
                    widoczna w widgecie.
                </p>
            </div>

            <FormProvider {...form}>
                <div className="flex flex-col gap-6">
                    <div
                        className={classNames(
                            "flex flex-col gap-2 rounded-2xl border p-4 transition-colors",
                            showValidationErrors && isOverLimit(visitRequiredValue)
                                ? "border-red-300 bg-red-50/40"
                                : "border-outline-mild"
                        )}
                    >
                        <label className="text-sm font-medium text-on-surface">Wymagane</label>
                        <p className="text-xs text-on-surface/50">
                            Dokumenty i rzeczy, które pacjent musi zabrać na wizytę.
                        </p>

                        <RichTextFormField
                            name="visitRequired"
                            maxLength={MAX_LENGTH}
                            placeholder="np. Skierowanie, dokument tożsamości, dokumentacja medyczna..."
                            hiddenOptions={[
                                MediaOptions.IMAGE,
                                MediaOptions.LINK,
                                TextOptions.HEADING2,
                                TextOptions.HEADING1,
                                TextOptions.PARAGRAPH,
                                TextOptions.REMOVE_LIST,
                            ]}
                        />

                        {showValidationErrors && isOverLimit(visitRequiredValue) && (
                            <div className="rounded-lg bg-red-100 px-3 py-2">
                                <p className="text-xs font-medium text-red-700">
                                    Skróć treść w polu „Wymagane” do maksymalnie {MAX_LENGTH} znaków.
                                </p>
                            </div>
                        )}
                    </div>

                    <div
                        className={classNames(
                            "flex flex-col gap-2 rounded-2xl border p-4 transition-colors",
                            showValidationErrors && isOverLimit(visitOptionalValue)
                                ? "border-red-300 bg-red-50/40"
                                : "border-outline-mild"
                        )}
                    >
                        <label className="text-sm font-medium text-on-surface">Opcjonalne</label>
                        <p className="text-xs text-on-surface/50">
                            Dodatkowe rzeczy, które mogą być pomocne podczas wizyty.
                        </p>

                        <RichTextFormField
                            name="visitOptional"
                            maxLength={MAX_LENGTH}
                            placeholder="np. Wyniki badań, lista przyjmowanych leków..."
                            hiddenOptions={[
                                MediaOptions.IMAGE,
                                MediaOptions.LINK,
                                TextOptions.HEADING2,
                                TextOptions.HEADING1,
                                TextOptions.PARAGRAPH,
                                TextOptions.REMOVE_LIST,
                            ]}
                        />

                        {showValidationErrors && isOverLimit(visitOptionalValue) && (
                            <div className="rounded-lg bg-red-100 px-3 py-2">
                                <p className="text-xs font-medium text-red-700">
                                    Skróć treść w polu „Opcjonalne” do maksymalnie {MAX_LENGTH} znaków.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <AppButton
                            onClick={handlePreview}
                            className="border border-outline-mild rounded-full !px-4 !py-2 text-sm font-medium text-on-surface hover:bg-on-surface/5 transition-colors bg-transparent"
                        >
                            <div className="flex items-center gap-2">
                                <EyeIcon className="w-4 h-4"/>
                                <span>Podgląd</span>
                            </div>
                        </AppButton>

                        <AppButton
                            onClick={handleSave}
                            disabled={isSubmitting}
                            className="rounded-full !px-5 !py-2 text-sm font-medium !bg-primary !text-white hover:!opacity-90 transition-opacity"
                        >
                            Zapisz
                        </AppButton>
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default VisitRequirementSection;
